import Cookies from "js-cookie";
import { React, useState, useEffect } from "react";

// external
import Navbar from "../../Components/Navbar";
import { fetchProfile } from "../../utils/fetchProfile";
import { modalStyle } from "../../Components/Modal/Index";
import DataGridUserManagement from "./DataGridUserManagement";
import EditDataModal from "./EditDataModal";
import AddDataModal from "./AddDataModal";
import ImportModal from "./ImportModal";
import {
  fetchExportDataUserManagement,
  fetchExportTemplateUserManagement,
} from "./DataUtil";
import { Button, TableCell, TableSortLabel } from "@mui/material";
import SearchBar from "../../Components/SearchBar";
import StatusDataModal from "../StoreMaster/StatusDataModal";
import { Slide, toast } from "react-toastify";
import "../../style/usermanagement.css";

const tableCellStyle = {
  whiteSpace: "nowrap",
  backgroundImage: "linear-gradient(#0ab9cc, #069dae)",
  color: "#FFF",
  fontWeight: "bold",
  position: "sticky",
  top: 0,
  zIndex: 1000,
  minWidth: 120,
  textAlign: "center",
};

// Functional component for UserManagement
function UserManagement() {
  // initial
  const accessToken = Cookies.get("access_token") || "";
  const [isLoading, setIsLoading] = useState(false);
  const [filteredRows, setFilteredRows] = useState([]);

  const [sortBy, setSortBy] = useState("full_name");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const itemsPerPageList = [5, 10, 50, 100];
  const [totalItems, setTotalItems] = useState("");

  const [notAllowAccessPage, setNotAllowAccessPage] = useState(false);
  const [dataProfile, setDataProfile] = useState([]);

  // create
  const [isModalOpen, setIsModalOpen] = useState(false);

  //import
  const [openDialogImport, setOpenDialogImport] = useState(false);

  const dataStatusOption = [
    {
      label: "Active",
      id: "1",
    },
    {
      label: "Inactive",
      id: "0",
    },
  ];
  const dataRoleCodeOption = [
    {
      label: "ADMIN",
      id: "A",
    },
    {
      label: "CO ADMIN",
      id: "COA",
    },
    {
      label: "TEAM LEADER",
      id: "TL",
    },
    {
      label: "DATA COLLECTOR",
      id: "13",
    },
    {
      label: "QC OFFICER",
      id: "14",
    },
  ];

  const blockedMapForAction = {
    SA: ["A", "SA"],
    A: ["A", "SA"],
    COA: ["A", "SA", "COA"],
  };

  const filteredOptionsForAction =
    blockedMapForAction[dataProfile.role_code]?.length > 0
      ? dataRoleCodeOption.filter(
          (role) => !blockedMapForAction[dataProfile.role_code].includes(role.id)
        )
      : [];

  const blockedMapForFilter = {
    SA: ["SA"],
    A: ["A", "SA"],
    COA: ["A", "SA", "COA"],
  };

  const filteredOptionsForFilter =
    blockedMapForFilter[dataProfile.role_code]?.length > 0
      ? dataRoleCodeOption.filter(
          (role) => !blockedMapForFilter[dataProfile.role_code].includes(role.id)
        )
      : [];

  const [newUserData, setNewUserData] = useState({
    full_name: "",
    user_name: "",
    password: "",
    role_code: "",
  });

  // edit or update
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editedUser, setEditedUser] = useState({});
  const [editUserId, setEditUserId] = useState(null);

  // selected
  const [selectedRoleCode, setSelectedRoleCode] = useState(
    localStorage.getItem("selectedRoleCode") || "all"
  );
  const [selectedStatus, setSelectedStatus] = useState(
    localStorage.getItem("selectedStatus") || "1"
  );

  async function fetchMainData() {
    const token = Cookies.get("access_token");

    setFilteredRows([]);
    setIsLoading(true);

    const queryParams = new URLSearchParams();

    queryParams.append("page", currentPage);
    queryParams.append("size", itemsPerPage);
    queryParams.append("sort_by", sortBy);
    queryParams.append("sort_order", sortOrder);

    if (selectedRoleCode && selectedRoleCode !== "all") {
      queryParams.append("role_code", selectedRoleCode);
    }

    if (selectedStatus && selectedStatus !== "all") {
      queryParams.append("user_status", selectedStatus);
    }

    const queryString = queryParams.toString();
    const apiUrl =
      `${process.env.REACT_APP_API_URL}/api/v1/user/users_with_roles` +
      (queryString ? `?${queryString}` : "");

    try {
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTotalItems(data.pagination_info.total);
        setFilteredRows(data.data);
      } else {
        setFilteredRows([]);
      }
    } catch (error) {
      setFilteredRows([]);
    }

    setIsLoading(false);
  }

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchMainData();
    }, 200);
    return () => {
      clearTimeout(handler);
    };
  }, [
    accessToken,
    currentPage,
    itemsPerPage,
    sortBy,
    sortOrder,
    selectedRoleCode,
    selectedStatus,
  ]);

  // read
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchProfile(accessToken, setDataProfile, setNotAllowAccessPage, ["TL", "13", "14"]);
    }, 200);
    return () => {
      clearTimeout(handler);
    };
  }, [accessToken]);

  // Handle change in the dropdown selection
  const handleRoleCodeChange = (event, newValue) => {
    let id = "";
    if (newValue) {
      id = newValue.id;
    }
    setSelectedRoleCode(id);
    localStorage.setItem("selectedRoleCode", id);
  };
  // Handle change in the dropdown selection
  const handleStatusChange = (event, newValue) => {
    let id = "";
    if (newValue) {
      id = newValue.id;
    }
    setSelectedStatus(id);
    localStorage.setItem("selectedStatus", id);
  };

  // Template Download
  const handleExportData = async () => {
    fetchExportDataUserManagement(
      accessToken,
      setIsLoading,
      selectedRoleCode,
      selectedStatus
    );
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setNewUserData({
      ...newUserData,
      [name]: value,
    });
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const requestBody = {
        ...newUserData,
      };
      // Check if any value in newUserData is an empty string
      if (Object.values(newUserData).some((value) => value === "")) {
        alert("Isi semua data.");
        setIsLoading(false);
        return; // Exit the function if any value is an empty string
      }

      const token = Cookies.get("access_token");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/user/create_user`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (response.status === 201) {
        setIsModalOpen(false);
        alert("User berhasil ditambahkan!");
        setIsModalOpen(false);
        fetchMainData();
      } else if (response.status === 400) {
        const data = await response.json();
        alert(data.detail);
      } else {
        alert("User gagal dibuat!");
      }
    } catch (error) {
      console.error("Error creating User:", error);
    }
    setIsLoading(false);
  };

  const handleEditClick = async (rows) => {
    setEditedUser(rows);
    setEditUserId(rows.id);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
  };

  const handleUpdateUser = async () => {
    setIsLoading(true);
    if (editUserId) {
      const token = Cookies.get("access_token");
      try {
        const { status, role_code, role_name, ...requestBody } = {
          ...editedUser,
        };
        // Check if any value in newUserData is an empty string
        if (Object.values(requestBody).some((value) => value === "")) {
          alert("Isi semua data.");
          setIsLoading(false);
          return; // Exit the function if any value is an empty string
        }
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/v1/user/${editUserId}`,
          {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
          }
        );

        if (response.status === 200) {
          alert("User berhasil diubah!");
        } else {
          alert("User gagal diubah!");
        }
      } catch (error) {
        console.error(error);
      }
      fetchMainData();
      handleCloseEditModal();
    }
    setIsLoading(false);
  };

  // status
  const [statusModalOpen, seStatusModalOpen] = useState(false);
  const [statusModalData, seStatusModalData] = useState(null);

  const handleOpenStatusModal = async (row) => {
    seStatusModalData(row);
    seStatusModalOpen(true);
  };

  const handleCloseStatusModal = () => {
    seStatusModalData(null);
    seStatusModalOpen(false);
  };

  const handleStatus = async () => {
    setIsLoading(true);
    let statusChange = "1";
    if (statusModalData.status === "1") {
      statusChange = "0";
    }
    if (statusModalData) {
      try {
        const token = Cookies.get("access_token");
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/v1/user/${statusModalData.id}/status?user_status=${statusChange}`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          alert("user status berhasil diubah!");
          fetchMainData();
          handleCloseStatusModal();
        } else {
          alert("Gagal mengubah status user!");
        }
      } catch (error) {
        console.error("Error:", error);
      }
    }
    setIsLoading(false);
  };

  //function export template
  const [fileUrl, setFileUrl] = useState("");
  const [excelFile, setExcelFile] = useState("");

  const handleImport = () => {
    setOpenDialogImport(true);
  };
  const handleCloseDialog = () => {
    setOpenDialogImport(false);
    setFileUrl("");
    setExcelFile("");
  };

  // export product master setup template
  async function ExportUserManagementTemplate() {
    setIsLoading(true);
    const blob = await fetchExportTemplateUserManagement(
      accessToken,
      "template",
      "api/v1/user/export/template/base",
      {}
    );
    if (blob) {
      // throw data as blob then download it
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Template User Management.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } else {
      toast.error("Gagal mendownload template User Management!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Slide,
      });
    }
    setIsLoading(false);
  }

  // function import
  async function ImportUserManagement() {
    try {
      const formData = new FormData();
      formData.append("file", excelFile);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/user/import/template`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formData,
        }
      );

      if (response.status === 201) {
        toast.success("Import User Management berhasil dibuat!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Slide,
        });
        // refresh
        fetchMainData();
        // setFileUrl('')

        // close
        setOpenDialogImport(false);
      } else if (response.status === 200) {
        // throw data as blob then download it when validation on excel got fail
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "User Management Error.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.error("Ada masalah di excel. Periksa kembali isi excel!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Slide,
        });
      } else if (response.status === 400) {
        const data = await response.json();
        toast.error(data.detail, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Slide,
        });
      } else {
        toast.error("Gagal mengimport User Management!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Slide,
        });
        console.error(
          "Failed to import User Management template:",
          response.statusText
        );
      }
    } catch (error) {
      console.error("Error importing User Management template:", error);
    }
  }

  const handleUpload = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    ImportUserManagement();
    setIsLoading(false);
  };

  // pagination
  const handlePageChange = (event, page) => {
    setCurrentPage(page + 1);
  };

  const handleChangeItemPerPageSelect = (e) => {
    setItemsPerPage(e.target.value);
    setCurrentPage(1);
  };

  // sorting
  const handleSort = (column) => {
    const isSorting = sortBy === column && sortOrder === "asc";
    setSortOrder(isSorting ? "desc" : "asc");
    setSortBy(column);
  };

  const sortTableCell = (label, sortByKey) => (
    <TableCell
      style={tableCellStyle}
      sx={{ border: 1, borderColor: "grey.300" }}
    >
      <TableSortLabel
        active={sortBy === sortByKey}
        direction={sortOrder}
        onClick={() => handleSort(sortByKey)}
        sx={{
          color: "#FFF !important",
          "&:hover": {
            color: "#FFF",
          },
          "& .MuiTableSortLabel-icon": {
            color: "#FFF !important",
          },
        }}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  if (notAllowAccessPage) {
    return (
      <div>
        <Navbar
          active="User Management"
          initial={dataProfile.initial}
          role_code={dataProfile.role_code}
        />
        <div>Not allow to access this page</div>
      </div>
    );
  }
  return (
    <div>
      <Navbar
        active="User Management"
        initial={dataProfile.initial}
        role_code={dataProfile.role_code}
      />
      <div className="content" style={{ padding: "1rem" }}>
        <div
          className="legend"
          style={{
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
          }}
        >
          {/* search bar */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            <SearchBar
              id={"role-code-autocomplete"}
              data={filteredOptionsForFilter}
              value={
                filteredOptionsForFilter.find(
                  (option) => option.id === selectedRoleCode
                ) ?? null
              }
              handleChange={handleRoleCodeChange}
              label={"Role Name"}
            />
            <SearchBar
              id={"status-autocomplete"}
              data={dataStatusOption}
              value={
                dataStatusOption.find(
                  (option) => option.id === selectedStatus
                ) ?? null
              }
              handleChange={handleStatusChange}
              label={"Status"}
            />
          </div>
          {/* button */}
          <div
            className="button-user"
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "5px",
              marginTop: "10px",
              marginLeft: "-15px",
            }}
          >
            <div className="spacing-one"></div>
            <Button
              onClick={() => setIsModalOpen(true)}
              sx={{
                backgroundImage: "linear-gradient(#0ab9cc, #069dae)",
                border: "1px solid hsl(186, 89.90%, 27.30%)",
                borderRadius: "4px",
                color: "#fff",
                fontSize: { xs: "14px", sm: "16px" },
                padding: "4px 10px",
                textTransform: "none",
                "&:hover": {
                  backgroundImage: "linear-gradient(#0991a2, #057d8c)",
                },
              }}
              disableRipple
              disabled={isLoading}
            >
              {isLoading ? "Loading..." : "Add User"}
            </Button>
            <Button
              onClick={() => handleImport()}
              sx={{
                backgroundImage: "linear-gradient(#0ab9cc, #069dae)",
                border: "1px solid hsl(186, 89.90%, 27.30%)",
                borderRadius: "4px",
                color: "#fff",
                fontSize: { xs: "14px", sm: "16px" },
                padding: "4px 10px",
                textTransform: "none",
                "&:hover": {
                  backgroundImage: "linear-gradient(#0991a2, #057d8c)",
                },
              }}
              disableRipple
              disabled={isLoading}
            >
              {isLoading ? "Loading..." : "Upload Users"}
            </Button>
            <Button
              onClick={handleExportData}
              sx={{
                backgroundImage: "linear-gradient(#0ab9cc, #069dae)",
                border: "1px solid hsl(186, 89.90%, 27.30%)",
                borderRadius: "4px",
                color: "#fff",
                fontSize: { xs: "14px", sm: "16px" },
                padding: "4px 10px",
                textTransform: "none",
                "&:hover": {
                  backgroundImage: "linear-gradient(#0991a2, #057d8c)",
                },
              }}
              disableRipple
              disabled={isLoading}
            >
              {isLoading ? "Loading..." : "Download"}
            </Button>
          </div>
          {/* modal add*/}
          <AddDataModal
            isLoading={isLoading}
            newUserData={newUserData}
            dataRoleCodeOption={filteredOptionsForAction}
            handleInputChange={handleInputChange}
            handleSubmit={handleSubmit}
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            modalStyle={modalStyle}
            dataProfile={dataProfile}
          />
          {/* modal Import*/}
          <ImportModal
            open={openDialogImport}
            onClose={handleCloseDialog}
            title={"Import User Management"}
            buttonTitle={"Upload"}
            fileUrl={fileUrl}
            handleSubmit={handleUpload}
            handleChange={{
              excel: setExcelFile,
              fileUrl: setFileUrl,
            }}
            handleExport={ExportUserManagementTemplate}
            isLoading={isLoading}
          />
          {/* modal edit */}
          <EditDataModal
            isLoading={isLoading}
            editedUser={editedUser}
            setEditedUser={setEditedUser}
            handleUpdateUser={handleUpdateUser}
            handleCloseEditModal={handleCloseEditModal}
            editModalOpen={editModalOpen}
            modalStyle={modalStyle}
          />
          {/* modal status */}
          <StatusDataModal
            isLoading={isLoading}
            modalStyle={modalStyle}
            statusModalOpen={statusModalOpen}
            handleCloseStatusModal={handleCloseStatusModal}
            handleStatus={handleStatus}
          />
        </div>
      </div>
      <div>
        <DataGridUserManagement
          loading={isLoading}
          rows={filteredRows}
          handleEditClick={handleEditClick}
          handleOpenStatusModal={handleOpenStatusModal}
          sortTableCell={sortTableCell}
          itemsPerPageList={itemsPerPageList}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          handlePageChange={handlePageChange}
          handleChangeItemPerPageSelect={handleChangeItemPerPageSelect}
          totalItems={totalItems}
          tableCellStyle={tableCellStyle}
          dataProfile={dataProfile}
        />
      </div>
    </div>
  );
}

export default UserManagement;
